import React, { useState, useEffect } from 'react';
import api from '../services/api';
import SearchableSelect from './shared/SearchableSelect';
import discoCodes from '../data/disco-codes.json';

const formatAmount = (amount) => {
    if (!amount) return '';
    return new Intl.NumberFormat('da-DK', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        useGrouping: true
    }).format(amount);
};

const SECTIONS = [
    {
        key: 'ansaettelse',
        icon: 'fas fa-briefcase',
        title: 'Ansættelse',
        description: 'Information om ansættelsesforhold og arbejdssted',
    },
    {
        key: 'loen',
        icon: 'fas fa-cog',
        title: 'Lønopsætning',
        description: 'Grundlæggende lønindstillinger',
    },
    {
        key: 'goder',
        icon: 'fas fa-gift',
        title: 'Goder',
        description: 'Personalegoder og tillæg',
    },
    {
        key: 'ferie',
        icon: 'fas fa-umbrella-beach',
        title: 'Ferieordning og noter',
        description: 'Ferie, omsorgsdage og øvrige bemærkninger',
    },
];

function EmployeeTemplateForm({ initialValues, onSave, onCancel, isSaving }) {
    const [template, setTemplate] = useState(initialValues);
    const [employeeFields, setEmployeeFields] = useState([]);
    const [dropdownItems, setDropdownItems] = useState({});
    const [salaryInput, setSalaryInput] = useState('');
    const [weeklyHoursInput, setWeeklyHoursInput] = useState('');
    const [monthlyHoursInput, setMonthlyHoursInput] = useState('');
    const [ftPercentageInput, setFtPercentageInput] = useState('');
    const [careDaysInput, setCareDaysInput] = useState(''); 
    const [error, setError] = useState(null);
    const [activeStep, setActiveStep] = useState(1);

    useEffect(() => {
        setTemplate(initialValues);
    }, [initialValues]);

    useEffect(() => {
        const fetchFieldsAndDropdowns = async () => {
            try {
                const [fieldsData, listsData] = await Promise.all([
                    api.get('/employee-fields'),
                    api.get('/dropdown-lists')
                ]);
                setEmployeeFields(fieldsData);
                // Hent alle dropdown-items for alle lister
                const itemsObj = {};
                await Promise.all(listsData.map(async (list) => {
                    const items = await api.get(`/dropdown-lists/${list.id}/items`);
                    itemsObj[list.id] = items;
                }));
                setDropdownItems(itemsObj);
            } catch (err) {
                setError('Kunne ikke hente felter og dropdowns');
            }
        };
        fetchFieldsAndDropdowns();
    }, []);

    useEffect(() => {
        setSalaryInput(template.salary !== '' && template.salary !== null && template.salary !== undefined ? formatAmount(template.salary) : '');
        setWeeklyHoursInput(template.weekly_hours !== '' && template.weekly_hours !== null && template.weekly_hours !== undefined ? formatAmount(template.weekly_hours) : '');
        setMonthlyHoursInput(template.monthly_hours !== '' && template.monthly_hours !== null && template.monthly_hours !== undefined ? formatAmount(template.monthly_hours) : '');
        setFtPercentageInput(template.ft_percentage !== '' && template.ft_percentage !== null && template.ft_percentage !== undefined ? formatAmount(template.ft_percentage) : '');
        setCareDaysInput(template.care_days !== '' && template.care_days !== null && template.care_days !== undefined ? formatAmount(template.care_days) : '');
    }, [template]);

    const getFieldConfig = (fieldName) => employeeFields.find(f => f.field_name === fieldName);
    // Felter til sektioner
    const sectionFields = {
        ansaettelse: [
            'company', 'department', 'title', 'dimension1', 'dimension2', 'dimension3', 'position_profile', 'job_function_code', 'accounting_group_code',
        ],
        loen: [
            'salary_group', 'salary', 'hourly_rate', 'employment_type', 'weekly_hours', 'monthly_hours', 'pension', 'pension_seniority_date',
        ],
        goder: [
            'free_phone', 'free_phone_amount', 'free_car', 'health_insurance', 'staff_association', 'lunch_arrangement',
        ],
        ferie: [
            'vacation_scheme', 'ft_percentage', 'vacation_days', 'ff_seniority_date', 'free_choice', 'care_days', 'other',
        ],
    };
    // Filtrer kun aktive felter
    Object.keys(sectionFields).forEach(key => {
        sectionFields[key] = sectionFields[key].filter(f => getFieldConfig(f)?.is_active);
    });

    const handleChange = (field, value) => {
        setTemplate(prev => ({ ...prev, [field]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(template);
    };

    const handleNextStep = () => setActiveStep(s => Math.min(s + 1, SECTIONS.length));
    const handlePrevStep = () => setActiveStep(s => Math.max(s - 1, 1));

    if (error) return <div className="error-message">{error}</div>;

    const renderSection = (sectionKey) => {
        const fields = sectionFields[sectionKey];
        if (!fields || fields.length === 0) return null;
        return (
            <div className="employee-section">
                <div className="section-header">
                    <i className={SECTIONS.find(s => s.key === sectionKey).icon}></i>
                    <div>
                        <h3>{SECTIONS.find(s => s.key === sectionKey).title}</h3>
                        <div className="section-description">{SECTIONS.find(s => s.key === sectionKey).description}</div>
                    </div>
                </div>
                <div className="section-content">
                    {fields.map(fieldName => {
                        const field = getFieldConfig(fieldName);
                        if (!field) return null;
                        if (field.field_type === 'dropdown' && field.dropdown_list_id && dropdownItems[field.dropdown_list_id]) {
                            return (
                                <div className="employee-form-group" key={field.field_name}>
                                    <label>{field.display_name}</label>
                                    <select
                                        value={template[field.field_name] || ''}
                                        onChange={e => handleChange(field.field_name, e.target.value)}
                                    >
                                        <option value="">Vælg {field.display_name.toLowerCase()}</option>
                                        {dropdownItems[field.dropdown_list_id].map(item => (
                                            <option key={item.id} value={item.value}>{item.display_text}</option>
                                        ))}
                                    </select>
                                </div>
                            );
                        }
                        if (field.field_type === 'boolean') {
                            return (
                                <div className="employee-form-group" key={field.field_name}>
                                    <label>{field.display_name}</label>
                                    <input
                                        type="checkbox"
                                        checked={!!template[field.field_name]}
                                        onChange={e => handleChange(field.field_name, e.target.checked)}
                                    />
                                </div>
                            );
                        }
                        if (field.field_type === 'date') {
                            return (
                                <div className="employee-form-group" key={field.field_name}>
                                    <label>{field.display_name}</label>
                                    <input
                                        type="date"
                                        value={template[field.field_name] || ''}
                                        onChange={e => handleChange(field.field_name, e.target.value)}
                                    />
                                </div>
                            );
                        }
                        // Special: DISCO-kode som søgbar select
                        if (field.field_name === 'job_function_code') {
                            return (
                                <div className="employee-form-group" key={field.field_name}>
                                    <label>{field.display_name}</label>
                                    <SearchableSelect
                                        options={discoCodes.disco_codes.map(code => ({
                                            id: code.code,
                                            name: `${code.code} - ${code.name}`
                                        }))}
                                        value={template[field.field_name]}
                                        onChange={value => handleChange(field.field_name, value)}
                                        placeholder="Søg efter DISCO-kode..."
                                    />
                                </div>
                            );
                        }
                        // Special: Beløb/nummer felter med formatering
                        if (field.field_name === 'salary') {
                            return (
                                <div className="employee-form-group" key={field.field_name}>
                                    <label>{field.display_name}</label>
                                    <input
                                        type="text"
                                        value={salaryInput}
                                        onChange={e => {
                                            let value = e.target.value.replace(/[^\d.,]/g, '');
                                            setSalaryInput(value);
                                        }}
                                        onBlur={e => {
                                            let value = e.target.value.replace(/\./g, '');
                                            value = value.replace(/,/g, '.');
                                            const numValue = parseFloat(value);
                                            setTemplate(prev => ({ ...prev, salary: isNaN(numValue) ? '' : numValue }));
                                            setSalaryInput(isNaN(numValue) ? '' : formatAmount(numValue));
                                        }}
                                    />
                                </div>
                            );
                        }
                        if (field.field_name === 'weekly_hours') {
                            return (
                                <div className="employee-form-group" key={field.field_name}>
                                    <label>{field.display_name}</label>
                                    <input
                                        type="text"
                                        value={weeklyHoursInput}
                                        onChange={e => {
                                            let value = e.target.value.replace(/[^\d.,]/g, '');
                                            setWeeklyHoursInput(value);
                                        }}
                                        onBlur={e => {
                                            let value = e.target.value.replace(/\./g, '');
                                            value = value.replace(/,/g, '.');
                                            const numValue = parseFloat(value);
                                            setTemplate(prev => ({ ...prev, weekly_hours: isNaN(numValue) ? '' : numValue }));
                                            setWeeklyHoursInput(isNaN(numValue) ? '' : formatAmount(numValue));
                                        }}
                                    />
                                </div>
                            );
                        }
                        if (field.field_name === 'monthly_hours') {
                            return (
                                <div className="employee-form-group" key={field.field_name}>
                                    <label>{field.display_name}</label>
                                    <input
                                        type="text"
                                        value={monthlyHoursInput}
                                        onChange={e => {
                                            let value = e.target.value.replace(/[^\d.,]/g, '');
                                            setMonthlyHoursInput(value);
                                        }}
                                        onBlur={e => {
                                            let value = e.target.value.replace(/\./g, '');
                                            value = value.replace(/,/g, '.');
                                            const numValue = parseFloat(value);
                                            setTemplate(prev => ({ ...prev, monthly_hours: isNaN(numValue) ? '' : numValue }));
                                            setMonthlyHoursInput(isNaN(numValue) ? '' : formatAmount(numValue));
                                        }}
                                    />
                                </div>
                            );
                        }
                        if (field.field_name === 'ft_percentage') {
                            return (
                                <div className="employee-form-group" key={field.field_name}>
                                    <label>{field.display_name}</label>
                                    <input
                                        type="text"
                                        value={ftPercentageInput}
                                        onChange={e => {
                                            let value = e.target.value.replace(/[^\d.,]/g, '');
                                            setFtPercentageInput(value);
                                        }}
                                        onBlur={e => {
                                            let value = e.target.value.replace(/\./g, '');
                                            value = value.replace(/,/g, '.');
                                            const numValue = parseFloat(value);
                                            setTemplate(prev => ({ ...prev, ft_percentage: isNaN(numValue) ? '' : numValue }));
                                            setFtPercentageInput(isNaN(numValue) ? '' : formatAmount(numValue));
                                        }}
                                    />
                                </div>
                            );
                        }
                        if (field.field_name === 'care_days') {
                            return (
                                <div className="employee-form-group" key={field.field_name}>
                                    <label>{field.display_name}</label>
                                    <input
                                        type="text"
                                        value={careDaysInput}
                                        onChange={e => {
                                            let value = e.target.value.replace(/[^\d.,]/g, '');
                                            setCareDaysInput(value);
                                        }}
                                        onBlur={e => {
                                            let value = e.target.value.replace(/\./g, '');
                                            value = value.replace(/,/g, '.');
                                            const numValue = parseFloat(value);
                                            setTemplate(prev => ({ ...prev, care_days: isNaN(numValue) ? '' : numValue }));
                                            setCareDaysInput(isNaN(numValue) ? '' : formatAmount(numValue));
                                        }}
                                    />
                                </div>
                            );
                        }
                        // Standard tekstfelt
                        return (
                            <div className="employee-form-group" key={field.field_name}>
                                <label>{field.display_name}</label>
                                <input
                                    type="text"
                                    value={template[field.field_name] || ''}
                                    onChange={e => handleChange(field.field_name, e.target.value)}
                                />
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    return (
        <form onSubmit={handleSubmit}>
            <div className="employee-modal-steps">
                {SECTIONS.map((section, idx) => (
                    <div key={section.key} className={`step ${activeStep >= idx + 1 ? 'active' : ''} ${activeStep > idx + 1 ? 'completed' : ''}`}>
                        <div className="step-number">
                            <i className={section.icon}></i>
                        </div>
                        <div className="step-label">{section.title}</div>
                    </div>
                ))}
            </div>
            <div className="employee-modal-content">
                {/* Navn og beskrivelse altid øverst */}
                {activeStep === 1 && (
                    <>
                        <div className="form-group">
                            <label>Navn:</label>
                            <input
                                type="text"
                                value={template.name}
                                onChange={e => handleChange('name', e.target.value)}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>Beskrivelse:</label>
                            <textarea
                                value={template.description}
                                onChange={e => handleChange('description', e.target.value)}
                            />
                        </div>
                    </>
                )}
                {renderSection(SECTIONS[activeStep - 1].key)}
            </div>
            <div className="employee-modal-footer">
                <div className="modal-navigation">
                    {activeStep > 1 && (
                        <button className="nav-btn prev-btn" type="button" onClick={handlePrevStep}>
                            <i className="fas fa-chevron-left"></i> Tilbage
                        </button>
                    )}
                </div>
                <div className="modal-actions">
                    <button className="employee-cancel-btn" type="button" onClick={onCancel}>Annuller</button>
                    {activeStep < SECTIONS.length ? (
                        <button className="employee-save-btn" type="button" onClick={handleNextStep}>
                            Næste <i className="fas fa-chevron-right"></i>
                        </button>
                    ) : (
                        <button className="employee-save-btn" type="submit" disabled={isSaving}>
                            {isSaving ? 'Gemmer...' : 'Gem skabelon'}
                        </button>
                    )}
                </div>
            </div>
        </form>
    );
}

export default EmployeeTemplateForm; 